import type {
	ApiError,
	ApiKey,
	CreateApiKeyRequest,
	CreateApiKeyResponse,
	CreateOrgAppRequest,
	DeleteApiKeyRequest,
	DeleteOrgAppRequest,
	OrgApp,
	UpdateOrgAppRequest,
} from "../types/apiManagement";

type SignRequest = (request: Request) => Promise<Request>;

interface ApiManagementClientConfig {
	signRequest: SignRequest;
}

type JwtPayload = Record<string, unknown> & {
	org_id?: string | number;
	organization_id?: string | number;
};

function decodeJwtSegment(segment: string): string {
	const normalized = segment.replace(/-/g, "+").replace(/_/g, "/");
	const padding = "=".repeat((4 - (normalized.length % 4 || 4)) % 4);
	const base64 = normalized + padding;

	if (typeof atob === "function") {
		return atob(base64);
	}

	const bufferCtor = (globalThis as Record<string, any>).Buffer as
		| undefined
		| {
				from(data: string, encoding: string): { toString(enc: string): string };
		  };

	if (bufferCtor) {
		return bufferCtor.from(base64, "base64").toString("utf-8");
	}

	throw new Error("No base64 decoder available in this environment");
}

function decodeJwtPayload(token: string): JwtPayload {
	const segments = token.split(".");
	if (segments.length < 2) {
		throw new Error("Invalid JWT structure");
	}

	const payload = decodeJwtSegment(segments[1]);
	return JSON.parse(payload) as JwtPayload;
}

async function getOrganizationId(
	config: ApiManagementClientConfig,
): Promise<string> {
	const dummyRequest = new Request("https://placeholder.invalid", {
		headers: { "Content-Type": "application/json" },
	});

	const signed = await config.signRequest(dummyRequest);
	const authHeader = signed.headers.get("Authorization");

	if (!authHeader || !authHeader.startsWith("Bearer ")) {
		throw new Error("Missing Authorization header on signed request");
	}

	const token = authHeader.slice("Bearer ".length).trim();
	if (!token) {
		throw new Error("Empty bearer token in Authorization header");
	}

	let organizationId: string | undefined;
	try {
		const payload = decodeJwtPayload(token);
		const rawOrgId = payload.org_id ?? payload.organization_id;
		if (typeof rawOrgId === "number") {
			organizationId = rawOrgId.toString();
		} else if (typeof rawOrgId === "string") {
			organizationId = rawOrgId;
		}
	} catch (error) {
		throw new Error(
			`Failed to decode access token for organization_id: ${(error as Error).message}`,
		);
	}

	if (!organizationId) {
		throw new Error("Access token is missing org_id claim");
	}

	return organizationId;
}

async function buildOrganizationPath(
	config: ApiManagementClientConfig,
	resourcePath: string,
): Promise<string> {
	const organizationId = await getOrganizationId(config);
	const trimmed = resourcePath.startsWith("/")
		? resourcePath.slice(1)
		: resourcePath;
	const path = `/organizations/${encodeURIComponent(organizationId)}/${trimmed}`;
	return path.startsWith("//") ? path.slice(1) : path;
}

const API_BASE_URL = process.env.ZUDOKU_PUBLIC_API_MANAGEMENT_URL;

if (!API_BASE_URL) {
	console.warn(
		"[apiManagementService] ZUDOKU_PUBLIC_API_MANAGEMENT_URL is not configured. Requests will fail until it is set.",
	);
}

async function apiRequest<T>(
	path: string,
	config: ApiManagementClientConfig,
	init: RequestInit = {},
): Promise<T> {
	if (!API_BASE_URL) {
		throw new Error("ZUDOKU_PUBLIC_API_MANAGEMENT_URL is not configured");
	}

	const url = new URL(path, API_BASE_URL);
	const request = new Request(url.toString(), {
		...init,
		headers: {
			"Content-Type": "application/json",
			...(init.headers || {}),
		},
	});

	const signed = await config.signRequest(request);
	const response = await fetch(signed);

	if (!response.ok) {
		let errorBody: unknown;
		try {
			errorBody = await response.json();
		} catch {
			errorBody = await response.text();
		}

		const error: ApiError = {
			message: `API request failed: ${response.status} ${response.statusText}`,
			status: response.status,
			detail: errorBody,
		};
		throw error;
	}

	if (response.status === 204) {
		return undefined as T;
	}

	const contentLength = response.headers.get("Content-Length");
	if (contentLength === "0") {
		return undefined as T;
	}

	const contentType = response.headers.get("Content-Type") ?? "";
	if (!contentType.includes("application/json")) {
		return undefined as T;
	}

	try {
		return (await response.json()) as T;
	} catch (error) {
		if (error instanceof SyntaxError) {
			return undefined as T;
		}
		throw error;
	}
}

function normalizeApiKeys(payload: unknown): ApiKey[] {
	if (!payload) {
		return [];
	}

	if (Array.isArray(payload)) {
		return payload.map(convertApiKey);
	}

	if (typeof payload === "object" && payload !== null) {
		const maybeArray =
			(payload as Record<string, unknown>).keys ??
			(payload as Record<string, unknown>).apikeys ??
			(payload as Record<string, unknown>).api_keys ??
			(payload as Record<string, unknown>).items;

		if (Array.isArray(maybeArray)) {
			return maybeArray.map(convertApiKey);
		}
	}

	return [];
}

function convertApiKey(raw: any): ApiKey {
	return {
		id: raw?.id ?? raw?.name ?? "",
		name: raw?.name ?? raw?.description ?? "",
		key: raw?.key ?? "",
		created_at: raw?.created_at ?? raw?.createdOn,
		expires_at: raw?.expires_at ?? raw?.expiry ?? raw?.expiresOn,
		updated_at: raw?.updated_at ?? raw?.updatedOn,
	};
}

export const apiManagementService = {
	async fetchApps(config: ApiManagementClientConfig): Promise<OrgApp[]> {
		const path = await buildOrganizationPath(config, "apps");
		return apiRequest<OrgApp[]>(path, config);
	},

	async fetchAppKeys(
		appId: string,
		config: ApiManagementClientConfig,
	): Promise<ApiKey[]> {
		const path = await buildOrganizationPath(
			config,
			`apps/${encodeURIComponent(appId)}/apikeys`,
		);
		const response = await apiRequest<unknown>(path, config);
		return normalizeApiKeys(response);
	},

	async createApp(
		payload: CreateOrgAppRequest,
		config: ApiManagementClientConfig,
	): Promise<OrgApp> {
		const path = await buildOrganizationPath(config, "app");
		return apiRequest<OrgApp>(path, config, {
			method: "POST",
			body: JSON.stringify(payload),
		});
	},

	async updateApp(
		payload: UpdateOrgAppRequest,
		config: ApiManagementClientConfig,
	): Promise<OrgApp> {
		const path = await buildOrganizationPath(config, "apps");
		return apiRequest<OrgApp>(path, config, {
			method: "PATCH",
			body: JSON.stringify(payload),
		});
	},

	async deleteApp(
		payload: DeleteOrgAppRequest,
		config: ApiManagementClientConfig,
	): Promise<void> {
		const path = await buildOrganizationPath(config, "apps");
		await apiRequest<void>(path, config, {
			method: "DELETE",
			body: JSON.stringify(payload),
		});
	},

	async createApiKey(
		payload: CreateApiKeyRequest,
		config: ApiManagementClientConfig,
	): Promise<CreateApiKeyResponse> {
		const path = await buildOrganizationPath(config, "apikeys");
		return apiRequest<CreateApiKeyResponse>(path, config, {
			method: "POST",
			body: JSON.stringify(payload),
		});
	},

	async deleteApiKey(
		payload: DeleteApiKeyRequest,
		config: ApiManagementClientConfig,
	): Promise<void> {
		const path = await buildOrganizationPath(config, "apikeys");
		await apiRequest<void>(path, config, {
			method: "DELETE",
			body: JSON.stringify(payload),
		});
	},
};

export type ApiManagementService = typeof apiManagementService;

export function createApiManagementClient(signRequest: SignRequest) {
	const config: ApiManagementClientConfig = { signRequest };
	return {
		fetchApps: () => apiManagementService.fetchApps(config),
		fetchAppKeys: (appId: string) =>
			apiManagementService.fetchAppKeys(appId, config),
		createApp: (payload: CreateOrgAppRequest) =>
			apiManagementService.createApp(payload, config),
		updateApp: (payload: UpdateOrgAppRequest) =>
			apiManagementService.updateApp(payload, config),
		deleteApp: (payload: DeleteOrgAppRequest) =>
			apiManagementService.deleteApp(payload, config),
		createApiKey: (payload: CreateApiKeyRequest) =>
			apiManagementService.createApiKey(payload, config),
		deleteApiKey: (payload: DeleteApiKeyRequest) =>
			apiManagementService.deleteApiKey(payload, config),
	};
}
